import React, { useState, useEffect } from 'react';
import { Compass } from 'lucide-react';
import { Button } from '../components/Button';
import { RadioCard } from '../components/RadioCard';
import { cn } from '../utils/cn';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { CoreValue } from '../types';
import { ProtectedLayout } from '../components/ProtectedLayout';

const predefinedValues: CoreValue[] = [
  'Achievement', 'Adventure', 'Authenticity', 'Boldness', 'Connection',
  'Contribution', 'Courage', 'Creativity', 'Curiosity', 'Dancing',
  'Discovery', 'Excellence', 'Exercise', 'Faith', 'Family',
  'Friendship', 'Fun', 'Growth', 'Helping Others', 'Imagination',
  'Integrity', 'Kindness', 'Movement', 'Music', 'Nature',
  'Passion', 'Play', 'Pleasure', 'Purpose', 'Responsibility',
  'Spontaneity', 'Structures', 'Success', 'Systems', 'Teaching', 'Wonder'
];

export function CoreValues() {
  const navigate = useNavigate();
  const [selectedValues, setSelectedValues] = useState<CoreValue[]>([]);
  const [customValue, setCustomValue] = useState('');
  const [activeCustomIndex, setActiveCustomIndex] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [coreWord, setCoreWord] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('core_word, name, email')
          .eq('id', user.id)
          .single();

        if (profile) {
          if (profile.core_word) setCoreWord(profile.core_word);
          if (profile.name) setFullName(profile.name);
          if (profile.email) setEmail(profile.email);
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchProfileData();
  }, []);

  const handleValueSelect = (value: CoreValue) => {
    if (selectedValues.includes(value)) {
      setSelectedValues(prev => prev.filter(v => v !== value));
    } else if (selectedValues.length < 3) {
      setSelectedValues(prev => [...prev, value]);
    }
  };

  const handleCustomValueChange = (value: string) => {
    setCustomValue(value);
  };

  const handleCustomValueConfirm = () => {
    if (customValue.trim() && selectedValues.length < 3) {
      setSelectedValues(prev => [...prev, customValue.trim()]);
      setCustomValue('');
      setActiveCustomIndex(null);
    }
  };

  const sendWebhook = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      console.log('🔑 Access Token:', accessToken);
      if (!accessToken) throw new Error('Missing access token');

      const webhookUrl = 'https://cdnbjnapvlbmarhuflsv.supabase.co/functions/v1/trigger-reminder';
      const [first_name = '', last_name = ''] = fullName.split(' ');

      const payload = {
        eventType: 'core_profile_update',
        payload: {
          full_name: fullName,
          first_name,
          last_name,
          email,
          core_word: coreWord,
          core_value_1: selectedValues[0],
          core_value_2: selectedValues[1],
          core_value_3: selectedValues[2]
        }
      };

      console.log('📦 Sending webhook payload:', payload);

      const res = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify(payload)
      });

      const resultText = await res.text();

      if (!res.ok) {
        console.error('❌ Webhook failed:', resultText);
      } else {
        console.log('✅ Webhook success:', resultText);
      }
    } catch (err) {
      console.error('🔥 Webhook error:', err);
    }
  };

  const handleNext = async () => {
    if (selectedValues.length !== 3 || isSubmitting) return;

    setIsSubmitting(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          core_value_1: selectedValues[0],
          core_value_2: selectedValues[1],
          core_value_3: selectedValues[2]
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      await sendWebhook();

      navigate('/core-value-confirmation');
    } catch (error: any) {
      console.error('❗ Error updating core values:', error);
      setError(error.message || 'Failed to update core values');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ProtectedLayout>
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-4xl w-full bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <Compass className="w-8 h-8 text-[#236caf]" />
            <h1 className="text-3xl font-bold text-[#004060]">Core Values</h1>
          </div>

          <p className="text-xl text-gray-600 mb-8">
            Great job. Now {coreWord} is a big broad word and means different things to different people. We want to know what it means to you. What are three words that help you experience more {coreWord} or describe what it means to you?
            {selectedValues.length > 0 && (
              <span className="text-[#236caf] font-medium ml-2">
                ({selectedValues.length}/3 selected)
              </span>
            )}
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
            {predefinedValues.map((value) => (
              <RadioCard
                key={value}
                label={value}
                selected={selectedValues.includes(value)}
                onClick={() => handleValueSelect(value)}
                className={cn(
                  selectedValues.length === 3 && !selectedValues.includes(value) && 'opacity-50 cursor-not-allowed'
                )}
              />
            ))}

            <div
              className={cn(
                'p-4 rounded-lg border-2 transition-all duration-200',
                activeCustomIndex === 0 ? 'border-[#236caf] bg-blue-50' : 'border-dashed border-gray-300',
                selectedValues.length === 3 && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div className="flex flex-col gap-2">
                <span className="text-lg font-medium text-gray-700">Other Value</span>
                <input
                  type="text"
                  value={customValue}
                  onChange={(e) => handleCustomValueChange(e.target.value)}
                  onFocus={() => setActiveCustomIndex(0)}
                  onBlur={handleCustomValueConfirm}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleCustomValueConfirm();
                  }}
                  placeholder="Enter custom value..."
                  className="w-full p-2 rounded border border-gray-300 focus:border-[#236caf] focus:ring-2 focus:ring-[#236caf]"
                  disabled={selectedValues.length === 3}
                />
              </div>
            </div>
          </div>

          {selectedValues.length > 0 && (
            <div className="mb-8 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-medium text-gray-700 mb-2">Selected Values:</h3>
              <div className="flex flex-wrap gap-2">
                {selectedValues.map((value, index) => (
                  <div
                    key={index}
                    className="px-3 py-1 bg-white rounded-full border border-[#236caf] text-[#236caf] flex items-center gap-2"
                  >
                    {value}
                    <button
                      onClick={() => setSelectedValues(prev => prev.filter(v => v !== value))}
                      className="text-[#236caf] hover:text-[#004060]"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button
            onClick={handleNext}
            disabled={selectedValues.length !== 3 || isSubmitting}
            className={cn(
              'w-full',
              (selectedValues.length !== 3 || isSubmitting) && 'opacity-50 cursor-not-allowed'
            )}
          >
            {isSubmitting ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </div>
    </ProtectedLayout>
  );
}
